import { ApiFetch } from '../../../shared/utils/fetch'
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap'
import type { ProductsResponse, ProductsParams, Product, ProductStatus, AttributeSchema, PlatformCreateProductDto, PlatformProduct, PlatformVariantDraft } from '../types'

export const productsApi = {
  // Published products
  getAll: async (params?: ProductsParams): Promise<ProductsResponse> => {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
    }

    const queryString = searchParams.toString();
    const endpoint = `/products${queryString ? `?${queryString}` : ""}`;

    const response = await ApiFetch.get<Product[] | ProductsResponse>(endpoint);
    if (Array.isArray(response)) {
      return {
        data: response,
        links: { first: '', last: '' },
        meta: {
          endCursor: '',
          hasNextPage: false,
          hasPreviousPage: false,
          lastCursor: '',
          startCursor: '',
          totalCount: response.length
        }
      };
    }
    return response;
  },

  getProductById: async (id: string): Promise<Product> => {
    const response = await ApiFetch.get<Enveloped<Product>>(`/products/${id}/admin`);
    return unwrap(response);
  },

  update: (id: string, formData: FormData): Promise<Product> => {
    return ApiFetch.put<Product>(`/products/${id}`, formData, true);
  },

  delete: (id: string): Promise<void> => {
    return ApiFetch.del<void>(`/products/${id}`);
  },

  // Draft products
  drafts: {
    getAll: async (params?: ProductsParams): Promise<ProductsResponse> => {
      const searchParams = new URLSearchParams();

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach((v) => searchParams.append(key, v));
            } else {
              searchParams.append(key, String(value));
            }
          }
        });
      }

      const queryString = searchParams.toString();
      const endpoint = `/products/drafts${queryString ? `?${queryString}` : ""}`;

      const response = await ApiFetch.get<Product[] | ProductsResponse>(endpoint);
      if (Array.isArray(response)) {
        return {
          data: response,
          links: { first: '', last: '' },
          meta: {
            endCursor: '',
            hasNextPage: false,
            hasPreviousPage: false,
            lastCursor: '',
            startCursor: '',
            totalCount: response.length
          }
        };
      }
      return response;
    },

    getById: async (id: string): Promise<Product> => {
      const response = await ApiFetch.get<Enveloped<Product>>(`/products/drafts/${id}`);
      return unwrap(response);
    },

    create: (formData: FormData): Promise<Product> => {
      return ApiFetch.post<Product>("/products/drafts", formData, true);
    },

    update: (id: string, formData: FormData): Promise<Product> => {
      return ApiFetch.put<Product>(`/products/drafts/${id}`, formData, true);
    },

    publish: (id: string): Promise<Product> => {
      return ApiFetch.post<Product>(`/products/drafts/${id}/publish`, {});
    },

    delete: (id: string): Promise<void> => {
      return ApiFetch.del<void>(`/products/drafts/${id}`);
    },
  },

  // Helper methods
  create: (formData: FormData): Promise<Product> => {
    return ApiFetch.post<Product>("/products", formData, true);
  },

  getByIdAndStatus: (id: string, status: ProductStatus): Promise<Product> => {
    if (status === 'DRAFT') {
      return productsApi.drafts.getById(id);
    }
    return productsApi.getProductById(id);
  },

  updateByStatus: (id: string, formData: FormData, currentStatus: ProductStatus): Promise<Product> => {
    if (currentStatus === 'DRAFT') {
      return productsApi.drafts.update(id, formData);
    }
    return productsApi.update(id, formData);
  },

  // Platform product-service calls (via osprey-api proxy)
  platform: {
    getAttributeSchemas: async (solutionId: string, tenantId?: string): Promise<AttributeSchema[]> => {
      const params = new URLSearchParams({ solutionId });
      if (tenantId) params.append('tenantId', tenantId);
      const response = await ApiFetch.get<Enveloped<AttributeSchema[]>>(`/attribute-schemas?${params.toString()}`);
      return unwrap(response);
    },

    createSchema: async (data: Omit<AttributeSchema, 'id' | 'variantValues'>): Promise<AttributeSchema> => {
      const response = await ApiFetch.post<Enveloped<AttributeSchema>>('/attribute-schemas', data);
      return unwrap(response);
    },

    updateSchema: async (id: string, data: Partial<Omit<AttributeSchema, 'id' | 'solutionId' | 'tenantId' | 'key'>>): Promise<AttributeSchema> => {
      const response = await ApiFetch.put<Enveloped<AttributeSchema>>(`/attribute-schemas/${id}`, data);
      return unwrap(response);
    },

    deleteSchema: (id: string): Promise<void> => {
      return ApiFetch.del<void>(`/attribute-schemas/${id}`);
    },

    createProduct: async (data: PlatformCreateProductDto): Promise<PlatformProduct> => {
      const response = await ApiFetch.post<Enveloped<PlatformProduct>>('/products', data);
      return unwrap(response);
    },

    createVariant: async (productId: string, data: Omit<PlatformVariantDraft, never>): Promise<unknown> => {
      const response = await ApiFetch.post<Enveloped<unknown>>(`/products/${productId}/variants`, data);
      return unwrap(response);
    },

    deleteProduct: (id: string): Promise<void> => {
      return ApiFetch.del<void>(`/products/${id}`);
    },

    deleteVariant: (id: string): Promise<void> => {
      return ApiFetch.del<void>(`/variants/${id}`);
    },
  },
};